import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Fab,
  Tooltip,
  TextField,
  Autocomplete,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import TimerIcon from '@mui/icons-material/Timer';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DescriptionSuggestField from '../Forms/DescriptionSuggestField';
import useFieldFocusReturn from '../../hooks/useFieldFocusReturn';
import { useTeamspace, useOptionsForTeamspace } from '../../context/TeamspaceProvider';
import { useAuth } from '../../context/AuthContext';
import { isRestrictedStaff } from '../../config/staffRestrictions';
import { API_BASE_URL, addMemberEmailToData } from '../../config/api';
// import { useTimerWebSocket } from '../../hooks/useTimerWebSocket';  // TEMPORARILY COMMENTED OUT - WebSocket implementation paused
import { useTimerSync } from '../../hooks/useTimerSync';
import { useFloatingButtonPosition, FAB_SIZE, PHONE_FAB_SIZE } from '../../hooks/useFloatingButtonPosition';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useIsPhone from '../../hooks/useIsPhone';
import { buildTagDescription, buildTagSuggestions, syncDescriptionWithTags } from '../../utils/tagDescription';

const FAB_GAP = 16; // space between the floating button and the running-timer panel
const PHONE_FAB_GAP = 12; // same, on phones

// Format elapsed time as HH:MM:SS
const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/**
 * The running clock. It ticks in its own state so the Timer (~2,200 lines, ~29 inputs)
 * no longer re-renders once a second while a timer runs (TMR-1). It is derived from
 * `startTime` alone, so every path that sets startTime -- Start, restore on load, adopting
 * the server's timer (useTimerSync), rollback after a failed save -- shows the right time
 * without an elapsed-seconds state to keep in step. `startTime` null shows 00:00:00.
 */
export const ElapsedClock = React.memo(function ElapsedClock({ startTime, color }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startTime) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startTime]);
  const seconds = startTime ? Math.max(0, Math.floor((now - startTime.getTime()) / 1000)) : 0;
  return (
    <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color }}>
      {formatTime(seconds)}
    </Typography>
  );
});

const Timer = ({ onTimeEntryAdded }) => {
  const { selectedTeamspaceId: globalSelectedTeamspaceId, teamspaces, teamspaceIdToNameMap, refreshTimeEntries, userPreferences } = useTeamspace();
  const { user, role_dict } = useAuth();
  const theme = useTheme();
  // Phones: the running timer spans from the left edge to the FAB (desktop: a
  // 320-400px card 112px from the right, which runs off a phone's left edge).
  const isPhone = useIsPhone();
  const { pathname } = useLocation();

  // Local state for form's teamspace selection (independent from global)
  const [formTeamspaceId, setFormTeamspaceId] = useState(globalSelectedTeamspaceId);

  // Cached options for form's teamspace (loaded when teamspace initializes or when form teamspace changes)
  const formOptions = useOptionsForTeamspace(formTeamspaceId);
  const formProjects = formOptions.projects;
  const formMembers = formOptions.members;
  const formTags = formOptions.tags;
  const formCategories = formOptions.categories;
  const formMemberLookup = formOptions.memberLookup;
  const formTagLookup = formOptions.tagLookup;
  const formLookupLoading = formOptions.loading;

  // Determine if the current user is 'staff' in the form's selected teamspace
  const formTeamspaceName = teamspaceIdToNameMap[formTeamspaceId];
  const isStaff = user && formTeamspaceName && role_dict[formTeamspaceName]?.role?.includes('Staff');
  // DCC/PH Staff may log time for other members too (2026-10-08) — only the
  // entries they can see stay limited to their own.
  const memberLocked = isStaff && !isRestrictedStaff(user, role_dict, formTeamspaceName);
  
  // Find current user's member ID for staff users
  const currentUserMember = useMemo(() => {
    if (!user || !formMembers.length) return null;
    return formMembers.find(member => 
      member.email === user.email || 
      member.username === user.username ||
      member.label === user.username ||
      member.label === user.email
    );
  }, [user, formMembers]);

  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const {
    position: fabPosition,
    onLeftHalf: fabOnLeftHalf,
    onTopHalf: fabOnTopHalf,
    viewport: fabViewport,
  } = useFloatingButtonPosition();
  const tagAutocompleteRef = useRef(null);
  const memberEntriesRef = useRef([]);
  
  // Refs for Autocomplete input fields to focus them when opened
  const memberInputRef = useRef(null);
  const projectInputRef = useRef(null);
  const tagInputRef = useRef(null);
  
  // Get default_billable from user preferences, default to true if not set
  const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;
  
  // Form state
  const [formData, setFormData] = useState({
    description: '',
    member: [],
    project: '',
    category: '',
    tag: [], // ManyToMany relationship - array of tag IDs
    billable: defaultBillable,
  });
  const [billableSetByProject, setBillableSetByProject] = useState(false);
  const [billableOverriddenFromProject, setBillableOverriddenFromProject] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);
  const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState(false);
  const [duplicateCheckDialogOpen, setDuplicateCheckDialogOpen] = useState(false);
  const [duplicateCheckInfo, setDuplicateCheckInfo] = useState(null);
  const [expandedFields, setExpandedFields] = useState({
    member: false,
    project: false,
    tag: false,
  });
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);
  const [showTeamspaceChangeDialog, setShowTeamspaceChangeDialog] = useState(false);
  const [pendingTeamspaceId, setPendingTeamspaceId] = useState(null);
  // Save & Stop outcome shown after the panel closes: { severity, message } | null
  const [saveNotice, setSaveNotice] = useState(null);
  
  // Track if this tab initiated the timer action (to avoid circular updates)
  const isLocalActionRef = useRef(false);

  // Helper to normalize formData tag field (ensure it's always an array)
  const normalizeFormData = (data) => {
    if (!data) return data;
    const normalized = { ...data };
    if (normalized.tag !== undefined && !Array.isArray(normalized.tag)) {
      normalized.tag = normalized.tag ? [normalized.tag] : [];
    }
    if (normalized.member !== undefined && !Array.isArray(normalized.member)) {
      normalized.member = normalized.member ? [String(normalized.member)] : [];
    }
    return normalized;
  };
  // Keep old name as alias for backwards compat with any remaining references
  const normalizeFormDataTag = normalizeFormData;

  // Options for the form's teamspace come from cache (useOptionsForTeamspace)

  // TEMPORARILY COMMENTED OUT - WebSocket implementation paused
  // WebSocket handlers
  // const handleWebSocketTimerStart = (startTimeStr, formDataFromWS) => {
  //   console.log('[Timer] 🔔 WebSocket: timer_started received', {
  //     startTime: startTimeStr,
  //     isLocalAction: isLocalActionRef.current,
  //     formData: formDataFromWS
  //   });
  //   // Only update if this wasn't initiated by this tab
  //   if (!isLocalActionRef.current) {
  //     console.log('[Timer] ✅ Updating timer from WebSocket (remote tab)');
  //     const savedStart = new Date(startTimeStr);
  //     const now = new Date();
  //     const elapsed = Math.floor((now - savedStart) / 1000);
  //     setStartTime(savedStart);
  //     setElapsedSeconds(elapsed);
  //     setIsRunning(true);
  //     if (formDataFromWS) {
  //       setFormData(normalizeFormDataTag(formDataFromWS));
  //     }
  //   } else {
  //     console.log('[Timer] ⏭️ Skipping update (local action)');
  //   }
  //   isLocalActionRef.current = false;
  // };

  // const handleWebSocketTimerStop = () => {
  //   console.log('[Timer] 🔔 WebSocket: timer_stopped received', {
  //     isLocalAction: isLocalActionRef.current
  //   });
  //   // Only update if this wasn't initiated by this tab
  //   if (!isLocalActionRef.current) {
  //     console.log('[Timer] ✅ Stopping timer from WebSocket (remote tab)');
  //     setIsRunning(false);
  //     setElapsedSeconds(0);
  //     setStartTime(null);
  //     setIsMinimized(false);
  //     setFormData({
  //       description: '',
  //       member: '',
  //       project: '',
  //       tag: [], // ManyToMany relationship - array of tag IDs
  //       billable: false,
  //     });
  //     setError(null);
  //     setValidationErrors({});
  //     setExpandedFields({ member: false, project: false, tag: false });
  //     localStorage.removeItem('timerState');
  //   } else {
  //     console.log('[Timer] ⏭️ Skipping stop (local action)');
  //   }
  //   isLocalActionRef.current = false;
  // };

  // const handleWebSocketTimerUpdate = (formDataFromWS) => {
  //   console.log('[Timer] 🔔 WebSocket: timer_updated received', {
  //     isLocalAction: isLocalActionRef.current,
  //     formData: formDataFromWS
  //   });
  //   // Only update if this wasn't initiated by this tab
  //   if (!isLocalActionRef.current && formDataFromWS) {
  //     console.log('[Timer] ✅ Updating form data from WebSocket (remote tab)');
  //     // Set flag to prevent sending WebSocket update back
  //     isLocalActionRef.current = true;
  //     setFormData(normalizeFormDataTag(formDataFromWS));
  //     // Reset flag after a short delay to allow form updates to complete
  //     setTimeout(() => {
  //       isLocalActionRef.current = false;
  //     }, 100);
  //   } else {
  //     console.log('[Timer] ⏭️ Skipping form update (local action or no data)');
  //     isLocalActionRef.current = false;
  //   }
  // };

  // Initialize WebSocket connection
  // const { sendMessage } = useTimerWebSocket(
  //   handleWebSocketTimerStart,
  //   handleWebSocketTimerStop,
  //   handleWebSocketTimerUpdate
  // );
  
  // Dummy sendMessage function to prevent errors
  const sendMessage = () => {
    // No-op - WebSocket implementation paused
  };

  // Convert lookup maps to array of options
  const availableProjects = useMemo(() => {
    if (!formProjects || !Array.isArray(formProjects)) {
      return [];
    }
    return formProjects
      .filter(p => !p.is_archived)
      .map(p => ({ id: p.id, label: p.full_project_name || p.name || '', isCategory: false, billable: p.billable }))
      .sort((a, b) => {
        const labelA = (a.label || '').toLowerCase();
        const labelB = (b.label || '').toLowerCase();
        return labelA.localeCompare(labelB);
      });
  }, [formProjects]);
  
  // Combine categories (first) and projects for the project dropdown
  const projectAndCategoryOptions = useMemo(() => {
    // Categories first, then projects
    return [...formCategories, ...availableProjects];
  }, [formCategories, availableProjects]);
  const memberOptions = useMemo(() => Object.entries(formMemberLookup).map(([id, label]) => ({ id, label })), [formMemberLookup]);
  const tagOptions = useMemo(() => Object.entries(formTagLookup).map(([id, label]) => ({ id, label })), [formTagLookup]);

  // Method to get current timer state
  const getCurrentTimerState = () => {
    if (isRunning && startTime) {
      const now = new Date();
      const duration = Math.floor((now - startTime) / 1000);
      return {
        startTime,
        endTime: now,
        duration,
        isRunning: true,
      };
    }
    return null;
  };

  // Method to stop timer programmatically
  const stopTimer = () => {
    if (isRunning && startTime) {
      setIsRunning(false);
      localStorage.removeItem('timerState');
      
      setStartTime(null);
      
      // Always set member to current user when stopping timer
      let defaultMember = '';
      if (currentUserMember) {
        defaultMember = currentUserMember.id;
      } else if (memberOptions.length > 0) {
        const usernameToMatch = user?.username || user?.email;
        const currentMember = memberOptions.find(m => 
          m.label === usernameToMatch || 
          m.label === user?.email ||
          m.label === user?.username
        );
        if (currentMember) {
          defaultMember = currentMember.id;
        }
      }
      
      // Reset form data
      setFormData({
        description: '',
        member: defaultMember ? [String(defaultMember)] : [],
        project: '',
        category: '',
        tag: [],
        billable: defaultBillable,
      });
      setError(null);
      setValidationErrors({});
      setExpandedFields({ member: false, project: false, tag: false });
    }
  };

  // Tag names offered alongside the recently-used descriptions, selected tags first
  const tagSuggestions = useMemo(
    () => buildTagSuggestions({ selectedTagIds: formData.tag, tagLookup: formTagLookup }),
    [formData.tag, formTagLookup]
  );

  // Helper: compute suggestions from raw entries based on project/category
  const computeDescriptionSuggestions = (entries, projectId, categoryId, memberId) => {
    const memberEntries = entries.filter(e => String(e.member) === String(memberId));
    let filtered = memberEntries;
    if (projectId || categoryId) {
      const projectFiltered = memberEntries.filter(e =>
        projectId ? String(e.project) === String(projectId) : String(e.category) === String(categoryId)
      );
      if (projectFiltered.length > 0) filtered = projectFiltered;
    }
    const seen = new Set();
    const result = [];
    for (const entry of filtered) {
      const desc = entry.description?.trim();
      if (desc && !seen.has(desc)) { seen.add(desc); result.push(desc); }
    }
    return result.slice(0, 20);
  };

  // Fetch recent entries for description suggestions when teamspace or user member changes
  useEffect(() => {
    memberEntriesRef.current = [];
    setDescriptionSuggestions([]);
    if (!formTeamspaceId || !currentUserMember?.id) return;
    const fetchDescriptions = async () => {
      try {
        const params = new URLSearchParams({ page_size: 100, member: currentUserMember.id, is_optimized: 'true' });
        const response = await axios.get(
          `${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/?${params.toString()}`
        );
        const entries = Array.isArray(response.data) ? response.data : (response.data.results || []);
        memberEntriesRef.current = entries;
        setDescriptionSuggestions(
          computeDescriptionSuggestions(entries, formData.project, formData.category, currentUserMember.id)
        );
      } catch (err) {
        console.warn('Failed to fetch description suggestions:', err);
      }
    };
    fetchDescriptions();
  }, [formTeamspaceId, currentUserMember]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute suggestions when project/category selection changes
  useEffect(() => {
    if (!currentUserMember?.id || !memberEntriesRef.current.length) return;
    setDescriptionSuggestions(
      computeDescriptionSuggestions(memberEntriesRef.current, formData.project, formData.category, currentUserMember.id)
    );
  }, [formData.project, formData.category]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize form teamspace when timer starts - always default to current global teamspace
  const prevIsRunningRef = useRef(isRunning);
  useEffect(() => {
    // When timer starts (isRunning changes from false to true), reset to global teamspace
    if (isRunning && !prevIsRunningRef.current && globalSelectedTeamspaceId) {
      setFormTeamspaceId(globalSelectedTeamspaceId);
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, globalSelectedTeamspaceId]);

  // Effect to clear member, project, and tag when form teamspace changes
  const prevFormTeamspaceIdRef = useRef(formTeamspaceId);
  useEffect(() => {
    // Only reset if teamspace actually changed (not on initial mount)
    if (formTeamspaceId && prevFormTeamspaceIdRef.current !== formTeamspaceId && prevFormTeamspaceIdRef.current !== null) {
      console.log('🔄 Timer teamspace changed from', prevFormTeamspaceIdRef.current, 'to', formTeamspaceId);
      
      // Clear member, project, category, and tag fields immediately
      setFormData(prev => ({
        ...prev,
        member: [], // Clear member field - will be set to NEW teamspace's member once loaded
        project: '',
        category: '',
        tag: [],
      }));
      setExpandedFields({ member: false, project: false, tag: false });
      setError(null);
      setValidationErrors({});
    }
    // Update ref to track current teamspace
    prevFormTeamspaceIdRef.current = formTeamspaceId;
  }, [formTeamspaceId]);

  // Effect to set member to current user after teamspace change and members are loaded
  // Also ensures member is always set to current user when members are loaded
  const prevTeamspaceForMemberRef = useRef(formTeamspaceId);
  useEffect(() => {
    // Check if teamspace actually changed
    const teamspaceChanged = prevTeamspaceForMemberRef.current !== formTeamspaceId && prevTeamspaceForMemberRef.current !== null;
    
    // Run if teamspace changed AND members are ready, OR if members just loaded and member is not set
    const shouldSetMember = (teamspaceChanged || formData.member.length === 0) &&
        !formLookupLoading &&
        (formMembers.length > 0 || memberOptions.length > 0) &&
        user &&
        formTeamspaceId;
    
    if (shouldSetMember) {
      if (teamspaceChanged) {
        console.log('🔄 Timer teamspace changed. Finding current user in NEW teamspace members');
      } else {
        console.log('🔄 Ensuring member is set to current user');
      }
      
      // Find current user in the teamspace's members
      let foundMember = null;
      if (currentUserMember) {
        foundMember = currentUserMember;
      } else if (formMembers.length > 0) {
        const usernameToMatch = user?.username || user?.email;
        foundMember = formMembers.find(member => 
          member.email === user?.email || 
          member.username === user?.username ||
          member.label === user?.username ||
          member.label === user?.email
        );
      } else if (memberOptions.length > 0) {
        const usernameToMatch = user?.username || user?.email;
        foundMember = memberOptions.find(m => 
          m.label === usernameToMatch || 
          m.label === user?.email ||
          m.label === user?.username
        );
      }
      
      if (foundMember) {
        const memberIdToSet = foundMember.id;
        console.log('✅ Found current user in teamspace members:', memberIdToSet);
        
        setFormData(prev => ({
          ...prev,
          member: [String(memberIdToSet)]
        }));
        
        prevTeamspaceForMemberRef.current = formTeamspaceId;
      } else {
        console.warn('⚠️ Could not find current user in teamspace members');
        if (teamspaceChanged) {
          prevTeamspaceForMemberRef.current = formTeamspaceId;
        }
      }
    }
  }, [formLookupLoading, formMembers, memberOptions, user, formTeamspaceId, currentUserMember, formData.member]);

  // Clear the running timer in this tab (Cancel here, or stopped in another tab/browser).
  const resetLocalTimer = () => {
    setIsRunning(false);
    localStorage.removeItem('timerState');
    setStartTime(null);
    setIsMinimized(false);
    setFormData({
      description: '',
      member: [],
      project: '',
      category: '',
      tag: [],
      billable: defaultBillable,
    });
    setError(null);
    setValidationErrors({});
    setExpandedFields({ member: false, project: false, tag: false });
    setBillableSetByProject(false);
    setBillableOverriddenFromProject(false);
  };

  // Adopt the server's timer — started or edited in another tab/browser (useTimerSync).
  const applyServerTimer = (timer) => {
    const match = teamspaces.find(ts => String(ts.id) === String(timer.teamspace_id));
    const tsId = match ? match.id : timer.teamspace_id;
    const fd = normalizeFormData({
      description: '',
      member: [],
      project: '',
      category: '',
      tag: [],
      billable: defaultBillable,
      ...(timer.form_data || {}),
    });
    const start = new Date(timer.start_time);
    // Prime the effects above so they read this as a synced timer, not as a fresh local start
    // (which would reset the teamspace to the global one) or a local teamspace switch (which
    // would clear member/project/tag).
    prevIsRunningRef.current = true;
    prevFormTeamspaceIdRef.current = tsId;
    prevTeamspaceForMemberRef.current = tsId;
    // Written before the state so "Reset form when timer starts" sees saved form data and keeps it.
    localStorage.setItem('timerState', JSON.stringify({
      isRunning: true,
      startTime: timer.start_time,
      formData: fd,
      teamspaceId: tsId,
      version: timer.version,
      memberEmail: user?.email,
    }));
    if (!isRunning) {
      setError(null);
      setValidationErrors({});
      // Started in another tab/browser, not here — show it collapsed.
      setIsMinimized(true);
    }
    setStartTime(start);
    setIsRunning(true);
    setFormTeamspaceId(tsId);
    setFormData(fd);
    return { formData: fd, teamspaceId: tsId };
  };

  const timerSync = useTimerSync({
    enabled: Boolean(user),
    isRunning,
    startTime,
    formData,
    teamspaceId: formTeamspaceId,
    onApply: applyServerTimer,
    onStopped: resetLocalTimer,
  });

  // Reset form when timer starts (only if not loading from localStorage)
  useEffect(() => {
    if (isRunning && startTime) {
      // Check if we have saved form data in localStorage
      const savedState = localStorage.getItem('timerState');
      if (savedState) {
        try {
          const { formData: savedFormData } = JSON.parse(savedState);
          if (savedFormData) {
            // Form data will be loaded from localStorage in the mount effect
            return;
          }
        } catch (e) {
          // If parsing fails, continue with reset
        }
      }
      
      // Always set member to current user
      let defaultMember = '';
      if (currentUserMember) {
        // For staff users, use currentUserMember from formMembers
        defaultMember = currentUserMember.id;
      } else if (memberOptions.length > 0) {
        // For non-staff or if currentUserMember not available, find in memberOptions
        const usernameToMatch = user?.username || user?.email;
        const currentMember = memberOptions.find(m => 
          m.label === usernameToMatch || 
          m.label === user?.email ||
          m.label === user?.username
        );
        if (currentMember) {
          defaultMember = currentMember.id;
        }
      }
      
      setFormData({
        description: '',
        member: defaultMember ? [String(defaultMember)] : [],
        project: '',
        category: '',
        tag: [],
        billable: defaultBillable,
      });
      setValidationErrors({});
      setError(null);
      setExpandedFields({ member: false, project: false, tag: false });
    }
  }, [isRunning, startTime, user, memberOptions, isStaff, currentUserMember, defaultBillable]);

  // Load timer state from localStorage — ONCE per tab, as soon as the user and teamspaces are known.
  // localStorage is shared by every tab of this browser, so re-reading it later would pick up a
  // timer another tab wrote, or this tab's own copy mid Save & Stop, and restart a timer that was
  // just saved. After the first read the timer only changes through this tab's own actions and
  // useTimerSync; later runs only keep the form's teamspace on the global one while none runs.
  const restoredSyncRef = useRef(false);
  useEffect(() => {
    if (restoredSyncRef.current) {
      if (!isRunning && globalSelectedTeamspaceId) setFormTeamspaceId(globalSelectedTeamspaceId);
      return;
    }
    if (!user?.email || teamspaces.length === 0) return; // not ready — this effect runs again
    restoredSyncRef.current = true;
    if (isRunning) return; // useTimerSync already adopted the server's timer, which is newer

    let savedState = localStorage.getItem('timerState');
    if (savedState && user?.email) {
      // localStorage outlives logout: a timer another account left in this browser isn't ours.
      try {
        const { memberEmail } = JSON.parse(savedState);
        if (memberEmail && memberEmail !== user.email) {
          localStorage.removeItem('timerState');
          savedState = null;
        }
      } catch (e) {
        // parsed again below
      }
    }
    if (savedState) {
      try {
        const { isRunning: savedIsRunning, startTime: savedStartTime, formData: savedFormData, teamspaceId: savedTeamspaceId, version: savedVersion } = JSON.parse(savedState);
        timerSync.restore(savedVersion, savedFormData && normalizeFormData(savedFormData), savedTeamspaceId);
        if (savedIsRunning && savedStartTime) {
          // A timer already running when this tab loads (new tab / reload) starts collapsed.
          setIsMinimized(true);
          // The sync hook's first read may have finished before this restore; check the server
          // again so a timer stopped elsewhere while this tab was closed is cleared.
          timerSync.refresh();
          const savedStart = new Date(savedStartTime);
          setStartTime(savedStart);
          setIsRunning(true);
          
          // Restore teamspace if saved
          if (savedTeamspaceId && teamspaces.some(ts => ts.id === savedTeamspaceId)) {
            setFormTeamspaceId(savedTeamspaceId);
          } else if (globalSelectedTeamspaceId) {
            setFormTeamspaceId(globalSelectedTeamspaceId);
          }
          
          // Load saved form data if it exists
          if (savedFormData) {
            // Normalize tag and member fields for backward compatibility
            const normalizedFormData = normalizeFormData(savedFormData);
            // Always ensure member includes current user
            if (currentUserMember && (!normalizedFormData.member || normalizedFormData.member.length === 0)) {
              normalizedFormData.member = [String(currentUserMember.id)];
            } else if (memberOptions.length > 0 && (!normalizedFormData.member || normalizedFormData.member.length === 0)) {
              const currentMember = memberOptions.find(m =>
                m.label === user?.email || m.label === user?.username
              );
              if (currentMember) normalizedFormData.member = [String(currentMember.id)];
            }
            setFormData(normalizedFormData);
          } else {
            // If no saved form data, set member to current user
            let defaultMember = null;
            if (currentUserMember) {
              defaultMember = currentUserMember.id;
            } else if (memberOptions.length > 0) {
              const currentMember = memberOptions.find(m =>
                m.label === user?.email || m.label === user?.username
              );
              if (currentMember) defaultMember = currentMember.id;
            }
            setFormData({
              description: '',
              member: defaultMember ? [String(defaultMember)] : [],
              project: '',
              category: '',
              tag: [],
              billable: defaultBillable,
            });
          }
        }
      } catch (e) {
        console.error('Error loading timer state:', e);
        localStorage.removeItem('timerState');
      }
    } else if (globalSelectedTeamspaceId) {
      // If no saved state, initialize with global teamspace
      setFormTeamspaceId(globalSelectedTeamspaceId);
    }
  }, [isStaff, currentUserMember, teamspaces, globalSelectedTeamspaceId, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Moving to another page collapses a running timer (Timer is mounted once at app level,
  // so a route change doesn't remount it). The first render is the load case, handled above.
  const prevPathnameRef = useRef(pathname);
  useEffect(() => {
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;
    if (isRunning) setIsMinimized(true);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save timer state and form data to localStorage
  useEffect(() => {
    if (isRunning && startTime) {
      localStorage.setItem('timerState', JSON.stringify({
        isRunning: true,
        startTime: startTime.toISOString(),
        formData: formData,
        teamspaceId: formTeamspaceId,
        // Server version this timer matches (null = not on the server yet) and whose it is.
        version: timerSync.version,
        memberEmail: user?.email,
      }));
    } else {
      localStorage.removeItem('timerState');
    }
  }, [isRunning, startTime, formData, formTeamspaceId, timerSync.version, user?.email]);

  // TEMPORARILY COMMENTED OUT - WebSocket implementation paused
  // Sync form data changes across tabs (debounced)
  // useEffect(() => {
  //   if (isRunning && startTime) {
  //     const timeoutId = setTimeout(() => {
  //       // Only send update if this wasn't from a WebSocket message
  //       if (!isLocalActionRef.current) {
  //         sendMessage('timer_update', {
  //           form_data: formData,
  //         });
  //       }
  //       isLocalActionRef.current = false;
  //     }, 500); // 500ms debounce

  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [formData, isRunning, startTime, sendMessage]);

  const handleStart = () => {
    if (isRunning) return; // Don't start if already running
    const now = new Date();
    console.log('[Timer] 🚀 Starting timer locally');
    isLocalActionRef.current = true;
    setStartTime(now);
    setIsRunning(true);
    setIsMinimized(false);

    // TEMPORARILY COMMENTED OUT - WebSocket implementation paused
    // Send WebSocket message to sync with other tabs
    // console.log('[Timer] 📤 Sending timer_start via WebSocket');
    // sendMessage('timer_start', {
    //   start_time: now.toISOString(),
    //   form_data: formData,
    // });
  };

  // Listen for external start timer event (from FloatingAddButton)
  useEffect(() => {
    const listener = () => {
      if (!isRunning) {
        const now = new Date();
        console.log('[Timer] 🚀 Starting timer from external event');
        isLocalActionRef.current = true;
        setStartTime(now);
        setIsRunning(true);
        // Started here, so open expanded (it may still be collapsed from the last timer).
        setIsMinimized(false);
      }
    };
    window.addEventListener('start-timer', listener);
    return () => window.removeEventListener('start-timer', listener);
  }, [isRunning]);

  // Handle click outside and ESC key to close tag dropdown
  useEffect(() => {
    if (!expandedFields.tag) return;

    const handleClickOutside = (event) => {
      // Check if click is outside the tag autocomplete and tag button/chip
      if (
        tagAutocompleteRef.current &&
        !tagAutocompleteRef.current.contains(event.target) &&
        !event.target.closest('[data-tag-button]') // Tag button/chip selector
      ) {
        setExpandedFields(prev => ({ ...prev, tag: false }));
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && expandedFields.tag) {
        setExpandedFields(prev => ({ ...prev, tag: false }));
      }
    };

    // Use mousedown instead of click to catch clicks before they register
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [expandedFields.tag]);

  const handleStop = () => {
    console.log('[Timer] 🛑 Stopping timer locally (Cancel)');
    isLocalActionRef.current = true;
    setIsRunning(false);
    if (startTime) {
      resetLocalTimer();
      // Stops it in every other tab/browser too
      timerSync.cancel();
    }
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAutocompleteChange = (name) => (event, value) => {
    if (name === 'tag') {
      // Handle multiple tags
      const tagIds = Array.isArray(value) ? value.map(v => v.id) : (value ? [value.id] : []);
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: tagIds,
        // Keep a tag-derived description in sync with the new tags; a
        // description the user typed themselves is left untouched.
        description: syncDescriptionWithTags({
          description: prevFormData.description,
          previousTagIds: prevFormData.tag,
          nextTagIds: tagIds,
          tagLookup: formTagLookup,
        }),
      }));
    } else if (name === 'project') {
      // Handle project/category selection - mutually exclusive
      if (value) {
        if (value.isCategory) {
          // Selected a category - set category and clear project, revert billable to default
          setFormData(prevFormData => ({
            ...prevFormData,
            category: value.id,
            project: '',
            billable: defaultBillable,
          }));
          setBillableSetByProject(false);
          setBillableOverriddenFromProject(false);
        } else {
          // Selected a project - set project and clear category, update billable from project
          setFormData(prevFormData => ({
            ...prevFormData,
            project: value.id,
            category: '',
            billable: value.billable,
          }));
          setBillableSetByProject(true);
          setBillableOverriddenFromProject(false);
        }
      } else {
        // Cleared selection - clear both, revert billable to default
        setFormData(prevFormData => ({
          ...prevFormData,
          project: '',
          category: '',
          billable: defaultBillable,
        }));
        setBillableSetByProject(false);
        setBillableOverriddenFromProject(false);
      }
    } else if (name === 'member') {
      const memberIds = Array.isArray(value) ? value.map(v => String(v.id)) : (value ? [String(value.id)] : []);
      setFormData(prevFormData => ({
        ...prevFormData,
        member: memberIds,
      }));
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: value ? value.id : ''
      }));
    }
  };

  const { fieldRowProps, closeField, fieldTabKeyDown } = useFieldFocusReturn(setExpandedFields);


  const toggleField = (field) => {
    console.log('🔘 [Timer] toggleField called for field:', field);
    console.log('🔘 [Timer] Current expandedFields state:', expandedFields);
    setExpandedFields(prev => {
      const newValue = !prev[field];
      console.log('🔘 [Timer] Setting expandedFields[' + field + '] to:', newValue);
      console.log('🔘 [Timer] New expandedFields state will be:', { ...prev, [field]: newValue });
      return { ...prev, [field]: newValue };
    });
  };

  const getSelectedLabel = (field, options, value) => {
    if (field === 'member') {
      if (!value || (Array.isArray(value) && value.length === 0)) return null;
      const memberIds = Array.isArray(value) ? value : [value];
      if (memberIds.length === 0) return null;
      if (memberIds.length === 1) {
        const option = options.find(o => String(o.id) === String(memberIds[0]));
        return option ? option.label : null;
      }
      return `(${memberIds.length} members)`;
    }
    if (field === 'tag' || field === 'tags') {
      // Handle array of tag IDs
      if (!value || (Array.isArray(value) && value.length === 0)) return null;
      const tagIds = Array.isArray(value) ? value : [value];
      if (tagIds.length === 0) return null;
      if (tagIds.length === 1) {
        const option = options.find(o => o.id === tagIds[0]);
        return option ? option.label : null;
      }
      return `(${tagIds.length} tags)`;
    }
    if (field === 'project') {
      // Handle project or category selection
      if (!value && !formData.category) return null;
      // Check category first
      if (formData.category) {
        const categoryOption = formCategories.find(c => String(c.id) === String(formData.category));
        if (categoryOption) return categoryOption.label;
      }
      // Then check project
      if (value) {
        const option = options.find(o => String(o.id) === String(value));
        return option ? option.label : null;
      }
      return null;
    }
    if (!value) return null;
    const option = options.find(o => o.id === value);
    return option ? option.label : null;
  };

  // Check if any fields that will be reset are selected
  const hasFieldsToReset = () => {
    // Check if member is set to current user by email (not by ID, since IDs differ per teamspace)
    let memberIsCurrentUser = false;
    
    const memberArray = Array.isArray(formData.member) ? formData.member : (formData.member ? [formData.member] : []);
    if (memberArray.length > 0 && user) {
      const allAreCurrentUser = memberArray.every(memberId => {
        if (formMembers.length > 0) {
          const memberEntry = formMembers.find(m => String(m.id) === String(memberId));
          if (memberEntry) {
            return memberEntry.email === user.email ||
                   memberEntry.email?.toLowerCase() === user.email?.toLowerCase();
          }
        }
        if (currentUserMember) return String(memberId) === String(currentUserMember.id);
        return false;
      });
      memberIsCurrentUser = allAreCurrentUser;
    }

    // Don't count member if it's the current user AND project, category, and tag are empty
    const hasMember = memberArray.length > 0 && !memberIsCurrentUser;
    const hasProject = formData.project && formData.project !== '';
    const hasCategory = formData.category && formData.category !== '';
    const hasTag = formData.tag && Array.isArray(formData.tag) && formData.tag.length > 0;
    
    // Only show confirmation if member is not current user, OR if project/category/tag are set
    return hasMember || hasProject || hasCategory || hasTag;
  };

  // Handle teamspace change with confirmation if needed
  const handleTeamspaceChange = (newTeamspaceId) => {
    if (hasFieldsToReset()) {
      setPendingTeamspaceId(newTeamspaceId);
      setShowTeamspaceChangeDialog(true);
    } else {
      setFormTeamspaceId(newTeamspaceId);
    }
  };

  // Handle confirmation dialog actions
  const handleConfirmTeamspaceChange = () => {
    if (pendingTeamspaceId) {
      setFormTeamspaceId(pendingTeamspaceId);
      setPendingTeamspaceId(null);
    }
    setShowTeamspaceChangeDialog(false);
  };

  const handleCancelTeamspaceChange = () => {
    setPendingTeamspaceId(null);
    setShowTeamspaceChangeDialog(false);
  };

  const validateForm = () => {
    const errors = {};
    // Check if member is set - for staff, it should be set automatically, for non-staff it's required
    if (formLookupLoading && formData.member.length === 0) {
      errors.member = 'Loading member information...';
    } else if (!isStaff && formData.member.length === 0) {
      errors.member = 'Member is required.';
    } else if (isStaff && formData.member.length === 0 && !currentUserMember) {
      errors.member = 'Loading member information...';
    }
    // Project is required in the UI even though time_entry.project is nullable.
    // The field is a single combined picker, so a time entry category satisfies it too.
    if (!formData.project && !formData.category) {
      errors.project = 'Project is required.';
    }
    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    return { isValid, errors };
  };

  const postTimeEntry = async (timeEntryData, teamspaceId, user) => {
    timeEntryData.teamspace = teamspaceId;
    const dataWithMemberEmail = addMemberEmailToData(timeEntryData, user);
    const apiUrl = `${API_BASE_URL}teamspaces/${teamspaceId}/time_entries/`;
    try {
      const response = await axios.post(apiUrl, dataWithMemberEmail);
      if (response.status !== 201) {
        const errorData = response.data;
        throw new Error(`API error: ${JSON.stringify(errorData)}`);
      }
      return response.data;
    } catch (error) {
      console.error("Failed to post time entry:", error);
      throw error;
    }
  };

  const checkForDuplicates = async (memberIds, startDateUTC, endDateUTC, basePayload) => {
    const params = new URLSearchParams({ start_date: startDateUTC, end_date: startDateUTC, timezone: 'UTC', page_size: 200 });
    const response = await axios.get(`${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/?${params.toString()}`);
    const entries = Array.isArray(response.data) ? response.data : (response.data.results || []);
    const targetEndMs = endDateUTC ? new Date(endDateUTC).getTime() : null;
    return memberIds.filter(memberId =>
      entries.some(entry => {
        if (String(entry.member) !== String(memberId)) return false;
        const entryEndMs = entry.end_time ? new Date(entry.end_time).getTime() : null;
        if (entryEndMs !== targetEndMs) return false;
        if (basePayload.project) { if (String(entry.project) !== String(basePayload.project)) return false; } else if (entry.project) return false;
        if (basePayload.category) { if (String(entry.category) !== String(basePayload.category)) return false; } else if (entry.category) return false;
        if ((entry.description || '') !== (basePayload.description || '')) return false;
        return true;
      })
    );
  };

  const doSaveAndStop = async (memberIds, basePayload) => {
    const savedStartTime = startTime;

    const currentState = getCurrentTimerState();
    if (!currentState || !currentState.startTime || !currentState.endTime) {
      setError('Timer state is invalid');
      return;
    }

    const startTimeUTC = new Date(currentState.startTime).toISOString();
    const endTimeUTC = new Date(currentState.endTime).toISOString();
    const calculatedDurationSeconds = Math.floor(
      (new Date(currentState.endTime).getTime() - new Date(currentState.startTime).getTime()) / 1000
    );

    // Take the timer off the server first, so the same timer can't be saved from two browsers.
    const claim = await timerSync.claim();
    if (claim === 'gone') {
      // Another tab or browser already stopped/saved it — saving here would duplicate the entry.
      stopTimer();
      timerSync.release({ retired: true });
      setSaveNotice({
        severity: 'info',
        message: 'This timer was already stopped in another tab or browser, so nothing was saved here.',
      });
      return;
    }
    let saved = false;
    try {
      saved = await saveClaimedTimer({ memberIds, basePayload, savedStartTime,
        startTimeUTC, endTimeUTC, calculatedDurationSeconds });
    } finally {
      // Saved -> this timer is finished for good; failed -> it was restored and gets re-created.
      timerSync.release({ retired: saved === true });
    }
  };

  const saveClaimedTimer = async ({ memberIds, basePayload, savedStartTime,
    startTimeUTC, endTimeUTC, calculatedDurationSeconds }) => {

    // Build one optimistic entry per member and add immediately
    const optimisticEntries = memberIds.map(memberId => {
      const memberEntry = formMembers.find(m => String(m.id) === String(memberId));
      const memberLabel = memberEntry?.label || formMemberLookup[String(memberId)] || '';
      return {
        id: `temp-${Date.now()}-${memberId}`,
        description: basePayload.description || '',
        billable: Boolean(basePayload.billable),
        member: memberId,
        member_username: memberLabel,
        project: basePayload.project || null,
        tag: basePayload.tag || [],
        teamspace: formTeamspaceId,
        start_time: startTimeUTC,
        end_time: endTimeUTC,
        duration: calculatedDurationSeconds,
        status: 'pending',
        rate: memberEntry?.rate || 0,
        currency: 'USD',
        amount: 0,
      };
    });

    for (const entry of optimisticEntries) {
      if (onTimeEntryAdded) onTimeEntryAdded(entry);
    }

    stopTimer();

    try {
      for (let i = 0; i < memberIds.length; i++) {
        const payload = { ...basePayload, member: memberIds[i],
          start_time: startTimeUTC, end_time: endTimeUTC };
        const result = await postTimeEntry(payload, formTeamspaceId, user);
        console.log('Time Entry successfully created:', result);
        const timeEntryData = result.time_entry || result;
        if (timeEntryData && timeEntryData.id) {
          window.dispatchEvent(new CustomEvent('time-entry-updated-from-api', {
            detail: { tempId: optimisticEntries[i].id, realEntry: timeEntryData }
          }));
        }
      }

      if (refreshTimeEntries) refreshTimeEntries();

      setSaveNotice({
        severity: 'success',
        message: memberIds.length > 1
          ? `${memberIds.length} time entries saved (${formatTime(calculatedDurationSeconds)} each).`
          : `Time entry saved (${formatTime(calculatedDurationSeconds)}).`,
      });

      setFormData({ description: '', member: [], project: '', category: '', tag: [], billable: defaultBillable });
      setError(null);
      setValidationErrors({});
      setExpandedFields({ member: false, project: false, tag: false });
      setBillableSetByProject(false);
      setBillableOverriddenFromProject(false);
      return true;
    } catch (err) {
      console.error('❌ [Timer] Error creating time entry:', err);
      const isNetworkError = !err.response && (
        err.message?.includes('Network Error') ||
        err.code === 'ERR_CONNECTION_REFUSED' ||
        err.code === 'ERR_NETWORK'
      );
      const responseStatus = err.response?.status;
      if (isNetworkError || (responseStatus && responseStatus !== 200 && responseStatus !== 201)) {
        setApiErrorDialogOpen(true);
        if (savedStartTime) { setStartTime(savedStartTime); setIsRunning(true); }
        return;
      }
      const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to save time entry. Please try again.';
      setError(errorMessage);
      if (savedStartTime) { setStartTime(savedStartTime); setIsRunning(true); }
    }
  };

  const handleSaveAndStop = async () => {
    isLocalActionRef.current = true;
    setError(null);
    setValidationErrors({});

    const validation = validateForm();
    if (!validation.isValid) {
      const errors = validation.errors;
      setError(Object.keys(errors).length > 0 ? Object.values(errors).join(', ') : 'Please fill in all required fields.');
      return;
    }

    if (formLookupLoading) {
      setError('Member information is still loading. Please wait a moment and try again.');
      return;
    }

    // Resolve member IDs — fall back to current user if array is empty
    let memberIds = formData.member.length > 0 ? formData.member : [];
    if (memberIds.length === 0) {
      if (currentUserMember) {
        memberIds = [String(currentUserMember.id)];
      } else {
        const foundMember = formMembers.find(m =>
          m.email === user?.email || m.username === user?.username || m.label === user?.username || m.label === user?.email
        ) || memberOptions.find(m => m.label === user?.email || m.label === user?.username);
        if (foundMember) memberIds = [String(foundMember.id)];
      }
    }

    if (memberIds.length === 0 && !isStaff) {
      setError('Member is required. Please select a member.');
      return;
    }
    if (memberIds.length === 0 && isStaff) {
      setError('Member information not yet loaded. Please wait a moment and try again.');
      return;
    }

    // Validate each member ID against current teamspace
    if (memberOptions.length > 0 || formMembers.length > 0) {
      memberIds = memberIds.map(memberId => {
        const exists = memberOptions.some(m => String(m.id) === String(memberId)) ||
                       formMembers.some(m => String(m.id) === String(memberId));
        if (!exists) {
          const corrected = currentUserMember?.id ||
            formMembers.find(m => m.email === user?.email || m.label === user?.email)?.id;
          return corrected ? String(corrected) : memberId;
        }
        return memberId;
      });
    }

    const trimmedDescription = formData.description?.trim();
    let description = formData.description || '';
    if (!trimmedDescription) {
      // Use all selected tag names as description if description is empty
      description = buildTagDescription(formData.tag, formTagLookup) || description;
    }

    const basePayload = {
      description,
      billable: Boolean(formData.billable),
      created_by: currentUserMember?.id,
      lastedited_by: currentUserMember?.id,
    };
    if (formData.category) basePayload.category = formData.category;
    else if (formData.project) basePayload.project = parseInt(formData.project, 10);
    if (formData.tag && Array.isArray(formData.tag) && formData.tag.length > 0) {
      basePayload.tag = formData.tag.map(id => parseInt(id, 10));
    }

    // Compute start/end for duplicate check
    const currentState = getCurrentTimerState();
    if (!currentState) { setError('Timer state is invalid'); return; }
    const startDateUTC = new Date(currentState.startTime).toISOString();
    const endDateUTC = new Date(currentState.endTime).toISOString();

    if (memberIds.length > 0) {
      try {
        const duplicateIds = await checkForDuplicates(memberIds, startDateUTC, endDateUTC, basePayload);
        if (duplicateIds.length > 0) {
          setDuplicateCheckInfo({ duplicateIds, memberIds, basePayload });
          setDuplicateCheckDialogOpen(true);
          return;
        }
      } catch (err) {
        console.error('Duplicate check failed, proceeding anyway:', err);
      }
    }

    await doSaveAndStop(memberIds, basePayload);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        // Sits beside the (draggable) FloatingAddButton, 16px from it, on whichever side
        // has more room, and aligned with its top or bottom edge by the same rule.
        // Phone: stretches across the free width between the screen edge and the
        // 56px button (16px edge margin, 12px gap) instead of a fixed-width card.
        ...(isPhone
          ? (fabOnLeftHalf
            ? { left: fabViewport.w - fabPosition.right + PHONE_FAB_GAP, right: 16 }
            : { left: 16, right: fabPosition.right + PHONE_FAB_SIZE + PHONE_FAB_GAP })
          : (fabOnLeftHalf
            ? { left: fabViewport.w - fabPosition.right + FAB_GAP }
            : { right: fabPosition.right + FAB_SIZE + FAB_GAP })),
        ...(fabOnTopHalf
          ? { top: fabViewport.h - fabPosition.bottom - (isPhone ? PHONE_FAB_SIZE : FAB_SIZE) }
          : { bottom: fabPosition.bottom }),
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: fabOnLeftHalf ? 'flex-start' : 'flex-end',
        gap: 1,
      }}
    >
      {isRunning && (
        <Paper
          elevation={6}
          sx={{
            p: 2,
            ...(isPhone ? {
              width: '100%',
              boxSizing: 'border-box',
              // Expanded form can outgrow a phone screen: scroll inside the card. The
              // card grows away from the button's edge, so what it has is the screen
              // less that offset (a sideways phone has very little to spare).
              maxHeight: `calc(100dvh - ${Math.max(88, (fabOnTopHalf ? fabViewport.h - fabPosition.bottom - PHONE_FAB_SIZE : fabPosition.bottom) + 16)}px)`,
              overflowY: 'auto',
            } : {
            minWidth: isMinimized ? 200 : 320,
            maxWidth: isMinimized ? 250 : 400,
            }),
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {/* Timer Display Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flex: 1 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Timer Running
              </Typography>
              <ElapsedClock startTime={isRunning ? startTime : null} color={theme.palette.text.primary} />
            </Box>
            <IconButton
              onClick={() => setIsMinimized(!isMinimized)}
              sx={{ color: theme.palette.text.primary }}
              size="small"
            >
              {isMinimized ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Box>

          {/* Error Message */}
          {error && (
            <Typography variant="caption" sx={{ color: '#ff5252', textAlign: 'center', mb: 1 }}>
              {error}
            </Typography>
          )}
          {/* Validation Errors */}
          {Object.keys(validationErrors).length > 0 && (
            <Box sx={{ mb: 1 }}>
              {Object.entries(validationErrors).map(([field, message]) => (
                <Typography key={field} variant="caption" sx={{ color: '#ff5252', display: 'block', textAlign: 'center' }}>
                  {message}
                </Typography>
              ))}
            </Box>
          )}

          {/* Form Fields - Only show when not minimized */}
          {!isMinimized && (
            <>
              {/* Description and Teamspace selector in same row (wraps on phones) */}
              <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1, mb: 1 }}>
                <DescriptionSuggestField
                  typeToOpen
                  size="small"
                  value={formData.description}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  suggestions={descriptionSuggestions}
                  tagSuggestions={tagSuggestions}
                  onSelect={(desc) => setFormData(prev => ({ ...prev, description: desc }))}
                  containerSx={{ flex: 3 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      color: theme.palette.text.primary,
                      '& fieldset': {
                        borderColor: theme.palette.info.main + '40',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.info.main + '80',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.info.main,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                  }}
                />
                <FormControl size="small" sx={{ flex: 1, minWidth: 120 }}>
                  <InputLabel 
                    id="timer-teamspace-select-label"
                    sx={{ color: 'rgba(125, 249, 255, 0.7)' }}
                  >
                    Teamspace
                  </InputLabel>
                  <Select
                    labelId="timer-teamspace-select-label"
                    id="timer-teamspace-select"
                    value={formTeamspaceId || ''}
                    label="Teamspace"
                    onChange={(e) => handleTeamspaceChange(e.target.value)}
                    sx={{
                      color: theme.palette.text.primary,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.info.main + '40',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.info.main + '80',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.info.main,
                      },
                      '& .MuiSvgIcon-root': {
                        color: theme.palette.text.primary,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: theme.palette.background.default,
                          color: theme.palette.text.primary,
                        },
                      },
                    }}
                  >
                    {teamspaces.map((teamspace) => (
                      <MenuItem key={teamspace.id} value={teamspace.id}>
                        {teamspace.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Icon buttons for Project, Tag, Billable, Member */}
              <Box {...fieldRowProps} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                {/* Project, Tag, Billable, Member container with loading overlay */}
                <Box sx={{ 
                  position: 'relative', 
                  display: 'inline-flex', 
                  flexWrap: 'wrap', 
                  alignItems: 'center', 
                  gap: 1,
                  minHeight: '48px',
                  padding: '4px',
                }}>
                  {/* Project Field */}
                  {!formData.project && !formData.category && (
                    <IconButton
                      data-field-control="project"
                      onClick={() => toggleField('project')}
                      sx={{ color: theme.palette.text.primary }}
                      size="small"
                      disabled={formLookupLoading}
                    >
                      <FolderIcon fontSize="small" />
                    </IconButton>
                  )}
                  {(formData.project || formData.category) && (
                    <Chip
                      icon={<FolderIcon sx={{ color: theme.palette.text.primary }} />}
                      label={getSelectedLabel('project', availableProjects, formData.project)}
                      data-field-control="project"
                      onClick={() => !formLookupLoading && toggleField('project')}
                      onDelete={!formLookupLoading ? () => {
                        setFormData(prev => ({ ...prev, project: '', category: '', billable: defaultBillable }));
                        setExpandedFields(prev => ({ ...prev, project: false }));
                        setBillableSetByProject(false);
                        setBillableOverriddenFromProject(false);
                      } : undefined}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.info.main + '1A',
                        color: theme.palette.text.primary,
                        border: '1px solid ' + theme.palette.info.main + '4D',
                        cursor: formLookupLoading ? 'default' : 'pointer',
                      }}
                    />
                  )}

                  {/* Tag Field */}
                  {(!formData.tag || (Array.isArray(formData.tag) && formData.tag.length === 0)) && (
                    <IconButton
                      data-tag-button
                      data-field-control="tag"
                      onClick={() => toggleField('tag')}
                      sx={{ color: theme.palette.text.primary }}
                      size="small"
                      disabled={formLookupLoading}
                    >
                      <LocalOfferIcon fontSize="small" />
                    </IconButton>
                  )}
                  {formData.tag && Array.isArray(formData.tag) && formData.tag.length > 0 && (
                    <Chip
                      data-tag-button
                      icon={<LocalOfferIcon sx={{ color: theme.palette.text.primary }} />}
                      label={getSelectedLabel('tag', tagOptions, formData.tag)}
                      data-field-control="tag"
                      onClick={() => !formLookupLoading && toggleField('tag')}
                      onDelete={!formLookupLoading ? () => {
                        setFormData(prev => ({
                          ...prev,
                          tag: [],
                          description: syncDescriptionWithTags({
                            description: prev.description,
                            previousTagIds: prev.tag,
                            nextTagIds: [],
                            tagLookup: formTagLookup,
                          }),
                        }));
                        setExpandedFields(prev => ({ ...prev, tag: false }));
                      } : undefined}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.info.main + '1A',
                        color: theme.palette.text.primary,
                        border: '1px solid ' + theme.palette.info.main + '4D',
                        cursor: formLookupLoading ? 'default' : 'pointer',
                      }}
                    />
                  )}

                {/* Billable Field */}
                <IconButton
                  onClick={() => {
                    if (billableSetByProject) setBillableOverriddenFromProject(true);
                    setFormData(prev => ({ ...prev, billable: !prev.billable }));
                    setBillableSetByProject(false);
                  }}
                  sx={{
                    color: formData.billable ? '#FFD700' : theme.palette.text.primary,
                    ...(billableSetByProject && {
                      boxShadow: '0 0 0 2px #f44336',
                    }),
                  }}
                  size="small"
                >
                  <Typography sx={{ fontSize: '1rem', fontWeight: formData.billable ? 'bold' : 'normal' }}>
                    $
                  </Typography>
                </IconButton>
                {billableOverriddenFromProject && (
                  <Typography variant="caption" sx={{ color: 'warning.main' }}>
                    Billable changed from project default
                  </Typography>
                )}

                {/* Member Field */}
                {formData.member.length === 0 && (
                  <IconButton
                    data-field-control="member"
                    onClick={() => toggleField('member')}
                    sx={{ color: theme.palette.text.primary }}
                    size="small"
                    disabled={formLookupLoading}
                  >
                    <PersonIcon fontSize="small" />
                  </IconButton>
                )}
                {formData.member.length > 0 && (
                  <Chip
                    icon={<PersonIcon sx={{ color: theme.palette.text.primary }} />}
                    label={getSelectedLabel('member', memberOptions, formData.member) || (currentUserMember?.label || user?.username || user?.email_alt)}
                    data-field-control="member"
                    onClick={() => !memberLocked && !formLookupLoading && toggleField('member')}
                    onDelete={!memberLocked && !formLookupLoading ? () => {
                      setFormData(prev => ({ ...prev, member: [] }));
                      setExpandedFields(prev => ({ ...prev, member: false }));
                    } : undefined}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.info.main + '1A',
                      color: theme.palette.text.primary,
                      border: '1px solid ' + theme.palette.info.main + '4D',
                      opacity: memberLocked ? 0.7 : 1,
                      cursor: memberLocked || formLookupLoading ? 'default' : 'pointer',
                    }}
                  />
                )}

                {/* Loading overlay for the whole row */}
                {formLookupLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      zIndex: 1000,
                      borderRadius: 1,
                    }}
                  >
                    <CircularProgress size={30} sx={{ color: theme.palette.info.main }} />
                  </Box>
                )}
                </Box>
              </Box>

              {/* Expandable Autocomplete fields */}
              {expandedFields.member && (
                <Box ref={memberInputRef}>
                  <Autocomplete
                    id="timer-member-autocomplete"
                    disabled={memberLocked}
                    disablePortal
                    onKeyDown={fieldTabKeyDown('member')}
                    options={memberOptions}
                    loading={formLookupLoading}
                    open
                    onOpen={() => {
                      console.log('📂 [Timer] Member Autocomplete onOpen triggered');
                      console.log('📂 [Timer] Current document.activeElement:', document.activeElement);
                      console.log('📂 [Timer] expandedFields.member:', expandedFields.member);
                      
                      setTimeout(() => {
                        console.log('📂 [Timer] Attempting to focus member input after 50ms delay...');
                        console.log('📂 [Timer] document.activeElement before focus:', document.activeElement);
                        
                        let input = null;
                        
                        const textFieldElement = document.getElementById('timer-member-input');
                        console.log('📂 [Timer] Method 1 - TextField element by ID:', !!textFieldElement, textFieldElement);
                        if (textFieldElement) {
                          input = textFieldElement.querySelector('input');
                          console.log('📂 [Timer] Method 1 - Input found in TextField:', !!input, input);
                        }
                        
                        if (!input) {
                          const autocompleteElement = document.getElementById('timer-member-autocomplete');
                          console.log('📂 [Timer] Method 2 - Autocomplete element by ID:', !!autocompleteElement, autocompleteElement);
                          if (autocompleteElement) {
                            input = autocompleteElement.querySelector('input');
                            console.log('📂 [Timer] Method 2 - Input found in Autocomplete:', !!input, input);
                          }
                        }
                        
                        if (!input) {
                          const allInputs = document.querySelectorAll('#timer-member-autocomplete input, #timer-member-input input');
                          console.log('📂 [Timer] Method 3 - Query selector found inputs:', allInputs.length, allInputs);
                          if (allInputs.length > 0) {
                            input = allInputs[0];
                            console.log('📂 [Timer] Method 3 - Using first input:', input);
                          }
                        }
                        
                        if (input) {
                          console.log('📂 [Timer] Input element found! Details:', {
                            id: input.id,
                            type: input.type,
                            disabled: input.disabled,
                            readOnly: input.readOnly,
                            tabIndex: input.tabIndex,
                            value: input.value,
                            className: input.className,
                            parentElement: input.parentElement?.tagName,
                          });
                          
                          try {
                            console.log('📂 [Timer] Attempting input.focus()...');
                            input.focus();
                            console.log('📂 [Timer] input.focus() called');
                            console.log('📂 [Timer] document.activeElement after focus:', document.activeElement);
                            console.log('📂 [Timer] input === document.activeElement:', input === document.activeElement);
                            console.log('📂 [Timer] Focus successful:', document.activeElement === input);
                            
                            if (input.value) {
                              console.log('📂 [Timer] Input has value, attempting input.select()...');
                              input.select();
                              console.log('📂 [Timer] input.select() called');
                            } else {
                              console.log('📂 [Timer] Input has no value, skipping select()');
                            }
                          } catch (error) {
                            console.error('📂 [Timer] Error focusing input:', error);
                          }
                        } else {
                          console.warn('📂 [Timer] ❌ Input element NOT FOUND after all methods');
                          console.warn('📂 [Timer] Available elements:', {
                            textField: !!textFieldElement,
                            autocomplete: !!document.getElementById('timer-member-autocomplete'),
                            allInputs: document.querySelectorAll('input').length,
                          });
                        }
                      }, 50);
                    }}
                    multiple
                    onClose={(event, reason) => {
                      console.log('📂 [Timer] Member Autocomplete onClose triggered, reason:', reason);
                      if (reason === 'blur' || reason === 'escape') {
                        closeField('member', reason);
                      }
                    }}
                    value={memberOptions.filter(option => formData.member.includes(String(option.id)))}
                    onChange={memberLocked ? undefined : handleAutocompleteChange('member')}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    filterOptions={(options, state) =>
                      options.filter(option => {
                        const l = (option && option.label) ? String(option.label) : '';
                        const q = (state && state.inputValue) ? String(state.inputValue) : '';
                        return l.toLowerCase().includes(q.toLowerCase());
                      })
                    }
                    renderTags={() => null}
                    sx={{ width: '100%' }}
                    renderInput={(params) => {
                      console.log('📂 [Timer] Member renderInput called, params:', {
                        hasInputProps: !!params.inputProps,
                        hasInputLabelProps: !!params.InputLabelProps,
                      });
                      return (
                        <TextField
                          {...params}
                          id="timer-member-input"
                          label="Member"
                          size="small"
                          autoFocus
                          disabled={memberLocked || formLookupLoading}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'off',
                          }}
                          inputRef={(ref) => {
                            console.log('📂 [Timer] Member inputRef callback called, ref:', ref);
                            if (params.inputRef) {
                              if (typeof params.inputRef === 'function') {
                                params.inputRef(ref);
                              } else if (params.inputRef.current !== undefined) {
                                params.inputRef.current = ref;
                              }
                            }
                            if (ref) {
                              memberInputRef.current = ref;
                              console.log('📂 [Timer] Member inputRef stored in memberInputRef.current');
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: theme.palette.background.default,
                              color: theme.palette.text.primary,
                            },
                          }}
                        />
                      );
                    }}
                  />
                </Box>
              )}
              {expandedFields.project && (
                <Box ref={projectInputRef}>
                  <Autocomplete
                    id="timer-project-autocomplete"
                    disablePortal
                    onKeyDown={fieldTabKeyDown('project')}
                    options={projectAndCategoryOptions}
                    loading={formLookupLoading}
                    open
                    onOpen={() => {
                      console.log('📁 [Timer] Project Autocomplete onOpen triggered');
                      console.log('📁 [Timer] Current document.activeElement:', document.activeElement);
                      console.log('📁 [Timer] expandedFields.project:', expandedFields.project);
                      
                      setTimeout(() => {
                        console.log('📁 [Timer] Attempting to focus project input after 50ms delay...');
                        console.log('📁 [Timer] document.activeElement before focus:', document.activeElement);
                        
                        let input = null;
                        
                        const textFieldElement = document.getElementById('timer-project-input');
                        console.log('📁 [Timer] TextField element by ID:', !!textFieldElement, textFieldElement);
                        if (textFieldElement) {
                          input = textFieldElement.querySelector('input');
                          console.log('📁 [Timer] Input found in TextField:', !!input, input);
                        }
                        
                        if (!input) {
                          const autocompleteElement = document.getElementById('timer-project-autocomplete');
                          console.log('📁 [Timer] Autocomplete element by ID:', !!autocompleteElement, autocompleteElement);
                          if (autocompleteElement) {
                            input = autocompleteElement.querySelector('input');
                            console.log('📁 [Timer] Input found in Autocomplete:', !!input, input);
                          }
                        }
                        
                        if (!input) {
                          const allInputs = document.querySelectorAll('#timer-project-autocomplete input, #timer-project-input input');
                          console.log('📁 [Timer] Query selector found inputs:', allInputs.length, allInputs);
                          if (allInputs.length > 0) {
                            input = allInputs[0];
                            console.log('📁 [Timer] Using first input:', input);
                          }
                        }
                        
                        if (input) {
                          console.log('📁 [Timer] Input element found! Details:', {
                            id: input.id,
                            type: input.type,
                            disabled: input.disabled,
                            readOnly: input.readOnly,
                            tabIndex: input.tabIndex,
                            value: input.value,
                          });
                          
                          try {
                            console.log('📁 [Timer] Attempting input.focus()...');
                            input.focus();
                            console.log('📁 [Timer] input.focus() called');
                            console.log('📁 [Timer] document.activeElement after focus:', document.activeElement);
                            console.log('📁 [Timer] Focus successful:', document.activeElement === input);
                            
                            if (input.value) {
                              console.log('📁 [Timer] Input has value, attempting input.select()...');
                              input.select();
                              console.log('📁 [Timer] input.select() called');
                            }
                          } catch (error) {
                            console.error('📁 [Timer] Error focusing input:', error);
                          }
                        } else {
                          console.warn('📁 [Timer] ❌ Input element NOT FOUND');
                        }
                      }, 50);
                    }}
                    onClose={(event, reason) => {
                      console.log('📁 [Timer] Project Autocomplete onClose triggered');
                      closeField('project', reason);
                    }}
                    value={
                      formData.category 
                        ? projectAndCategoryOptions.find(option => option.id === formData.category && option.isCategory) || null
                        : (formData.project 
                            ? projectAndCategoryOptions.find(option => option.id === formData.project && !option.isCategory) || null
                            : null)
                    }
                    onChange={handleAutocompleteChange('project')}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, state) =>
                      options.filter(option => {
                        const l = (option && option.label) ? String(option.label) : '';
                        const q = (state && state.inputValue) ? String(state.inputValue) : '';
                        return l.toLowerCase().includes(q.toLowerCase());
                      })
                    }
                    sx={{ width: '100%' }}
                    renderInput={(params) => {
                      console.log('📁 [Timer] Project renderInput called');
                      return (
                        <TextField
                          {...params}
                          id="timer-project-input"
                          label="Project"
                          size="small"
                          autoFocus
                          disabled={formLookupLoading}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'off',
                          }}
                          inputRef={(ref) => {
                            console.log('📁 [Timer] Project inputRef callback called, ref:', ref);
                            if (params.inputRef) {
                              if (typeof params.inputRef === 'function') {
                                params.inputRef(ref);
                              } else if (params.inputRef.current !== undefined) {
                                params.inputRef.current = ref;
                              }
                            }
                            if (ref) {
                              projectInputRef.current = ref;
                              console.log('📁 [Timer] Project inputRef stored');
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: theme.palette.background.default,
                              color: theme.palette.text.primary,
                            },
                          }}
                        />
                      );
                    }}
                  />
                </Box>
              )}
              {expandedFields.tag && (
                <Box ref={tagAutocompleteRef}>
                  <Autocomplete
                    id="timer-tag-autocomplete"
                    multiple
                    disablePortal
                    onKeyDown={fieldTabKeyDown('tag')}
                    options={tagOptions}
                    loading={formLookupLoading}
                    open={expandedFields.tag}
                    onOpen={() => {
                      console.log('🏷️ [Timer] Tag Autocomplete onOpen triggered');
                      console.log('🏷️ [Timer] Current document.activeElement:', document.activeElement);
                      console.log('🏷️ [Timer] expandedFields.tag:', expandedFields.tag);
                      
                      setTimeout(() => {
                        console.log('🏷️ [Timer] Attempting to focus tag input after 50ms delay...');
                        console.log('🏷️ [Timer] document.activeElement before focus:', document.activeElement);
                        
                        let input = null;
                        
                        const textFieldElement = document.getElementById('timer-tag-input');
                        console.log('🏷️ [Timer] TextField element by ID:', !!textFieldElement, textFieldElement);
                        if (textFieldElement) {
                          input = textFieldElement.querySelector('input');
                          console.log('🏷️ [Timer] Input found in TextField:', !!input, input);
                        }
                        
                        if (!input) {
                          const autocompleteElement = document.getElementById('timer-tag-autocomplete');
                          console.log('🏷️ [Timer] Autocomplete element by ID:', !!autocompleteElement, autocompleteElement);
                          if (autocompleteElement) {
                            input = autocompleteElement.querySelector('input');
                            console.log('🏷️ [Timer] Input found in Autocomplete:', !!input, input);
                          }
                        }
                        
                        if (!input) {
                          const allInputs = document.querySelectorAll('#timer-tag-autocomplete input, #timer-tag-input input');
                          console.log('🏷️ [Timer] Query selector found inputs:', allInputs.length, allInputs);
                          if (allInputs.length > 0) {
                            input = allInputs[0];
                            console.log('🏷️ [Timer] Using first input:', input);
                          }
                        }
                        
                        if (input) {
                          console.log('🏷️ [Timer] Input element found! Details:', {
                            id: input.id,
                            type: input.type,
                            disabled: input.disabled,
                            readOnly: input.readOnly,
                            tabIndex: input.tabIndex,
                            value: input.value,
                          });
                          
                          try {
                            console.log('🏷️ [Timer] Attempting input.focus()...');
                            input.focus();
                            console.log('🏷️ [Timer] input.focus() called');
                            console.log('🏷️ [Timer] document.activeElement after focus:', document.activeElement);
                            console.log('🏷️ [Timer] Focus successful:', document.activeElement === input);
                            
                            if (input.value) {
                              console.log('🏷️ [Timer] Input has value, attempting input.select()...');
                              input.select();
                              console.log('🏷️ [Timer] input.select() called');
                            }
                          } catch (error) {
                            console.error('🏷️ [Timer] Error focusing input:', error);
                          }
                        } else {
                          console.warn('🏷️ [Timer] ❌ Input element NOT FOUND');
                        }
                      }, 50);
                    }}
                    onClose={(event, reason) => {
                      console.log('🏷️ [Timer] Tag Autocomplete onClose triggered, reason:', reason);
                      // Close on blur, escape, or toggle
                      if (reason === 'blur' || reason === 'escape' || reason === 'toggleInput') {
                        closeField('tag', reason);
                      }
                    }}
                    value={Array.isArray(formData.tag) && formData.tag.length > 0
                      ? tagOptions.filter(option => formData.tag.includes(option.id))
                      : []}
                    onChange={handleAutocompleteChange('tag')}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={(options, state) =>
                      options.filter(option => {
                        const l = (option && option.label) ? String(option.label) : '';
                        const q = (state && state.inputValue) ? String(state.inputValue) : '';
                        return l.toLowerCase().includes(q.toLowerCase());
                      })
                    }
                    sx={{ width: '100%' }}
                    renderInput={(params) => {
                      console.log('🏷️ [Timer] Tag renderInput called');
                      return (
                        <TextField
                          {...params}
                          id="timer-tag-input"
                          label="Tags (optional)"
                          size="small"
                          autoFocus
                          disabled={formLookupLoading}
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'off',
                          }}
                          inputRef={(ref) => {
                            console.log('🏷️ [Timer] Tag inputRef callback called, ref:', ref);
                            if (params.inputRef) {
                              if (typeof params.inputRef === 'function') {
                                params.inputRef(ref);
                              } else if (params.inputRef.current !== undefined) {
                                params.inputRef.current = ref;
                              }
                            }
                            if (ref) {
                              tagInputRef.current = ref;
                              console.log('🏷️ [Timer] Tag inputRef stored');
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: theme.palette.background.default,
                              color: theme.palette.text.primary,
                            },
                          }}
                        />
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                  />
                </Box>
              )}

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={handleStop}
                  sx={{
                    flex: 1,
                    bgcolor: theme.palette.error.main,
                    '&:hover': {
                      bgcolor: theme.palette.error.dark,
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAndStop}
                  sx={{
                    flex: 1,
                    bgcolor: theme.palette.success.main, // Assuming a success color for save
                    color: theme.palette.success.contrastText,
                    '&:hover': {
                      bgcolor: theme.palette.success.dark,
                    },
                  }}
                >
                  Save & Stop
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}
      
      {/* Confirmation dialog for teamspace change */}
      <Dialog open={showTeamspaceChangeDialog} onClose={handleCancelTeamspaceChange}>
        <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
          Confirm Teamspace Change
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
          <Typography>
            Changing the teamspace will reset the Member, Project, Category, and Tag fields. 
            All other fields (description, billable) will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
          <Button 
            onClick={handleCancelTeamspaceChange} 
            variant="contained" 
            sx={{ bgcolor: theme.palette.error.main, color: theme.palette.error.contrastText, '&:hover': { bgcolor: theme.palette.error.dark } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmTeamspaceChange} 
            variant="contained" 
            sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Duplicate check dialog */}
      <Dialog open={duplicateCheckDialogOpen} onClose={() => setDuplicateCheckDialogOpen(false)}>
        <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
          Duplicate Time Entries Detected
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
          <Typography sx={{ mb: 1 }}>
            The following members already have a time entry for this period:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {(duplicateCheckInfo?.duplicateMemberIds || []).map(mid => {
              const opt = memberOptions.find(o => String(o.id) === String(mid));
              return <Chip key={mid} label={opt ? opt.label : mid} size="small" />;
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
          <Button
            onClick={() => setDuplicateCheckDialogOpen(false)}
            variant="outlined"
            sx={{ color: theme.palette.text.primary, borderColor: theme.palette.info.main }}
          >
            Cancel
          </Button>
          {duplicateCheckInfo && duplicateCheckInfo.nonDuplicateMemberIds && duplicateCheckInfo.nonDuplicateMemberIds.length > 0 && (
            <Button
              onClick={() => {
                setDuplicateCheckDialogOpen(false);
                doSaveAndStop(duplicateCheckInfo.nonDuplicateMemberIds, duplicateCheckInfo.basePayload);
              }}
              variant="contained"
              sx={{ bgcolor: theme.palette.warning.main, color: theme.palette.warning.contrastText, '&:hover': { bgcolor: theme.palette.warning.dark } }}
            >
              Skip Duplicates
            </Button>
          )}
          <Button
            onClick={() => {
              setDuplicateCheckDialogOpen(false);
              doSaveAndStop(duplicateCheckInfo.allMemberIds, duplicateCheckInfo.basePayload);
            }}
            variant="contained"
            sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}
          >
            Create All Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error dialog for API failures after optimistic update */}
      <Dialog open={apiErrorDialogOpen} onClose={() => setApiErrorDialogOpen(false)}>
        <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
          Error Saving Time Entry
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
          <Typography sx={{ color: theme.palette.text.primary, mb: 2 }}>
            There was an issue with saving the time entry. The entry may have been added to the table, but it may not have been saved correctly.
          </Typography>
          <Typography sx={{ color: theme.palette.text.primary }}>
            Please reload the page and try again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
          <Button 
            onClick={() => window.location.reload()} 
            variant="contained" 
            sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': { bgcolor: theme.palette.primary.dark } }}
          >
            Reload Page
          </Button>
          <Button 
            onClick={() => setApiErrorDialogOpen(false)} 
            variant="outlined"
            sx={{ color: theme.palette.text.primary, borderColor: theme.palette.info.main, '&:hover': { borderColor: theme.palette.info.dark } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!saveNotice}
        autoHideDuration={4000}
        onClose={(event, reason) => { if (reason !== 'clickaway') setSaveNotice(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={saveNotice?.severity || 'success'} onClose={() => setSaveNotice(null)} variant="filled" sx={{ fontSize: 13 }}>
          {saveNotice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Timer;

